import { useEffect, useState } from "react"
import { CommentNotification, NotificationPage, NOTIFICATIONS_CHANGED, notificationsRequest } from "./notificationService"

export function useCommentNotifications(uid: string, open: boolean, unreadOnly: boolean) {
  const [count, setCount] = useState(0)
  const [items, setItems] = useState<CommentNotification[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pages, setPages] = useState(1)
  const [revision, setRevision] = useState(0)
  useEffect(() => { setItems([]); setLoading(true); setPages(1) }, [uid, open, unreadOnly])
  useEffect(() => {
    let active = true; let running = false
    if (!uid) { setCount(0); setItems([]); setLoading(false); return }
    const refresh = async () => {
      if (!active || running || document.hidden) return
      running = true
      setError(false)
      // Neither endpoint should prevent the other from showing fresh data.
      const badge = notificationsRequest<{ count: number }>("count").then(result => {
        if (active) setCount(result.count)
      }).catch(() => { if (active) setError(true) })
        .finally(() => { if (active && !open) setLoading(false) })
      const loadList = async () => {
        if (!open) return
        const loaded: CommentNotification[] = []
        let cursor: NotificationPage["cursor"] = null
        try {
          for (let page = 0; page < pages; page++) {
            if (!active) return
            const result: NotificationPage = await notificationsRequest("list", { unreadOnly, cursor })
            if (!active) return
            loaded.push(...result.items); cursor = result.cursor
            if (!cursor) break
          }
          setItems(Array.from(new Map(loaded.map(item => [item.id, item])).values())); setHasMore(!!cursor)
        } catch { if (active) setError(true) }
        finally { if (active) setLoading(false) }
      }
      await Promise.allSettled([badge, loadList()])
      running = false
    }
    void refresh()
    const timer = setInterval(refresh, 10000)
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", refresh)
    const changed = () => setRevision(value => value + 1)
    window.addEventListener(NOTIFICATIONS_CHANGED, changed)
    return () => { active = false; clearInterval(timer); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); window.removeEventListener(NOTIFICATIONS_CHANGED, changed) }
  }, [uid, open, unreadOnly, pages, revision])
  return { count, items, hasMore, loading, error, refresh: () => { setLoading(true); setRevision(value => value + 1) }, loadMore: () => { setLoading(true); setPages(value => value + 1) } }
}
